/**
 * Turns whatever a failed request threw into an error a person can read in
 * Sentry.
 *
 * WHY
 *
 * CAPACITOR-9 is one line long: `Error: [object Object]`. It came from
 *
 *   catchError((error) => { ...; throw new Error(error); })
 *
 * where `error` is Angular's HttpErrorResponse. `new Error(object)` stringifies
 * its argument, and an object stringifies to `[object Object]`, so the status,
 * the URL and the reason are all destroyed at the exact moment they were about
 * to be reported. The report then says that something failed somewhere, which
 * is the least useful sentence a monitoring tool can produce.
 *
 * WHAT IS KEPT, AND WHAT IS DELIBERATELY NOT
 *
 * The status, the status text and the URL. Those name the failure and are not
 * personal data.
 *
 * Not the response body. It is the one part that can carry someone's email,
 * their position or a payment reference, and Sentry is not the place for that.
 * The original error is attached as `cause`, so a developer with the device in
 * hand still has everything; the message that travels carries only what is
 * needed to act.
 *
 * STATUS 0 IS A CASE OF ITS OWN
 *
 * XMLHttpRequest reports a network failure, a CORS refusal and a cancelled
 * request all as status 0 with an empty body. Writing "HTTP 0" would send the
 * next reader looking for a server that never answered. It is named for what
 * it is: no response at all.
 */

/** The shape of Angular's HttpErrorResponse, without importing @angular/common/http. */
export interface HttpErrorLike {
  status?: number;
  statusText?: string;
  url?: string | null;
  message?: string;
  name?: string;
}

const isHttpErrorLike = (error: unknown): error is HttpErrorLike =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  typeof (error as HttpErrorLike).status === 'number';

/**
 * True when a call failed with no response at all: status 0.
 *
 * On a phone this is almost always the network, a lift, a tunnel, a weak 4G
 * signal, or the app sent to the background mid-request. CAPACITOR-1B is one:
 * the same person loaded the same group three times in the minute before, and
 * the failing request never reached the API. Nothing in the code can fix that,
 * so it is not a defect to report.
 */
export const noResponseCameBack = (error: unknown): boolean => isHttpErrorLike(error) && error.status === 0;

/** The path, without the host, so two environments group as one issue. */
function pathOf(url?: string | null): string | undefined {
  const raw = url?.trim();
  if (!raw) return undefined;
  try {
    return new URL(raw).pathname;
  } catch {
    // A relative URL, which is already the path.
    return raw;
  }
}

/** One line naming the failure, safe to send. */
export function describeFailure(error: unknown): string {
  if (isHttpErrorLike(error)) {
    const path = pathOf(error.url);
    const where = path ? ` on ${path}` : '';
    if (error.status === 0) {
      // No response: offline, DNS, CORS, or a request the browser cancelled.
      return `no response from the server${where}`;
    }
    const statusText = error.statusText?.trim();
    const reason = statusText && statusText !== 'OK' ? ` ${statusText}` : '';
    return `HTTP ${error.status}${reason}${where}`;
  }
  if (error instanceof Error) {
    return error.message || error.name || 'unknown error';
  }
  if (typeof error === 'string' && error.trim()) {
    return error.trim();
  }
  // Never "[object Object]": that is the whole point of this file.
  return 'unknown error';
}

/**
 * An Error worth reporting: says what was being done and what went wrong, and
 * keeps the original as its cause.
 */
export function toReportableError(error: unknown, whatFailed: string): Error {
  if (error instanceof Error && !isHttpErrorLike(error)) {
    // Already a real error with a real message and a real stack. Wrapping it
    // would only move the stack one frame away from where it happened.
    return error;
  }
  const reported = new Error(`${whatFailed}: ${describeFailure(error)}`);
  (reported as Error & { cause?: unknown }).cause = error;
  return reported;
}
